<template>
  <div class="processing-progress-bar position-relative">
    <progress-bar :color="progressBarColor" :value="progressValue" />
    <div class="overlay caption white--text">{{ overlayLabel }}</div>
    <div class="caption grey--text text--darken-2 mt-1">
      {{ stageLabelPrefix }}{{ stageLabelSuffix }}
    </div>
  </div>
</template>

<script>
import ProgressBar from '@/components/Common/ProgressBar';
import { ReferenceFile } from '@/models';
import { UploadState } from '@/enums';

// NOTE: Order of the keys matter as they are used to derive the stage number shown on screen.
const UPLOAD_STATE_TO_STAGE_SUFFIX = {
  [UploadState.EXTRACTING]: 'Extracting the reference file',
  [UploadState.PROCESSING]: (referenceFile) => {
    const {
      recordsProcessedCount,
      recordsParsedCount,
    } = referenceFile;
    const processed = recordsProcessedCount?.toLocaleString() ?? '0';
    const total = recordsParsedCount?.toLocaleString() ?? '0';
    return `${processed}/${total} processed`;
  },
  [UploadState.DETECTING_DUPLICATES]: 'Finding potential duplicates',
  [UploadState.PENDING]: 'Waiting for processing',
  [UploadState.COMPLETE]: (referenceFile) => {
    // autoResolvedDuplicateCount is fuzzy-only (DOI/title matches); add the
    // separately-tracked identical duplicates for the total removed here.
    const duplicates =
      (referenceFile.autoResolvedDuplicateCount || 0) +
      (referenceFile.identicalDuplicateCount || 0);
    return [
      `${referenceFile.newReferenceCount.toLocaleString()} added`,
      `${referenceFile.invalidCount.toLocaleString()} invalid`,
      `${duplicates.toLocaleString()} duplicates`,
    ].join(', ');
  },
};

const UPLOAD_STATE_TO_OVERLAY_LABEL = {
  [UploadState.ERROR]: 'Internal error',
  [UploadState.EXTRACTING_FAILED]: 'Invalid File',
  [UploadState.COMPLETE]: 'Complete',
  [UploadState.TIMED_OUT]: 'Timed Out',
};

export default {
  name: 'ProcessingProgressBar',
  components: { ProgressBar },
  props: {
    referenceFile: {
      type: ReferenceFile,
      required: true,
    },
    fakeProgressIntervalMs: {
      type: Number,
      default: 100, // 100ms
    },
    fakeProgressSpeed: {
      type: Number,
      default: 10000,
    },
  },
  data: () => ({
    fakeProgressInterval: null,
    fakeProgressTime: 0,
    fakeProgress: 0,
  }),
  computed: {
    isStage() {
      return (
        this.referenceFile.uploadState in UPLOAD_STATE_TO_STAGE_SUFFIX
      );
    },
    stageLabelPrefix() {
      const uploadState = this.referenceFile.uploadState;
      const isPending = uploadState === UploadState.PENDING;
      const isComplete = uploadState === UploadState.COMPLETE;
      if (!isPending && !isComplete && this.isStage) {
        const index = Object.keys(
          UPLOAD_STATE_TO_STAGE_SUFFIX
        ).indexOf(uploadState);
        return `Stage: (${index + 1}/3): `;
      }
      return '';
    },
    stageLabelSuffix() {
      if (this.isStage) {
        const stageLabel =
          UPLOAD_STATE_TO_STAGE_SUFFIX[
            this.referenceFile.uploadState
          ];
        return typeof stageLabel === 'function'
          ? stageLabel(this.referenceFile)
          : stageLabel;
      }
      return '';
    },
    overlayLabel() {
      return UPLOAD_STATE_TO_OVERLAY_LABEL[
        this.referenceFile.uploadState
      ];
    },
    progressBarColor() {
      switch (this.referenceFile.uploadState) {
        case UploadState.ERROR:
        case UploadState.EXTRACTING_FAILED:
        case UploadState.TIMED_OUT:
          return 'error';
        case UploadState.COMPLETE:
          return 'success';
        default:
          return 'primary';
      }
    },
    processingProgress() {
      const processed =
        this.referenceFile?.recordsProcessedCount || 0;
      const total = this.referenceFile?.recordsParsedCount || 0;
      return total > 0 ? processed / total : 0;
    },
    progressValue() {
      if (this.referenceFile.isDone()) {
        return 100;
      }
      return this.shouldUseFakeProgress
        ? this.fakeProgress * 90
        : this.processingProgress * 100;
    },
    shouldUseFakeProgress() {
      return [
        UploadState.EXTRACTING,
        UploadState.DETECTING_DUPLICATES,
      ].includes(this.referenceFile.uploadState);
    },
  },
  watch: {
    referenceFile: function (
      { uploadState: prevUploadState },
      { uploadState }
    ) {
      if (prevUploadState !== uploadState) {
        this.fakeProgressTime = 0;
        this.fakeProgress = 0;
        if (!this.fakeProgressInterval) {
          this.startFakeProgressIfApplicable();
        }
      }
    },
  },
  mounted() {
    this.startFakeProgressIfApplicable();
  },
  beforeDestroy() {
    this.stopFakeProgress();
  },
  methods: {
    startFakeProgressIfApplicable() {
      if (this.shouldUseFakeProgress) {
        this.startFakeProgress();
      }
    },
    startFakeProgress() {
      this.resetFakeProgress();
      this.fakeProgressInterval = setInterval(
        this.onFakeProgressUpdate,
        this.fakeProgressIntervalMs
      );
    },
    onFakeProgressUpdate() {
      this.fakeProgressTime += this.fakeProgressIntervalMs;
      this.fakeProgress =
        1 - Math.exp(-this.fakeProgressTime / this.fakeProgressSpeed);
    },
    resetFakeProgress() {
      this.fakeProgressInterval = null;
      this.fakeProgressTime = 0;
    },
    stopFakeProgress() {
      clearInterval(this.fakeProgressInterval);
      this.resetFakeProgress();
    },
  },
};
</script>

<style lang="scss" scoped>
.processing-progress-bar {
  min-width: 245px;
}
.position-relative {
  position: relative;
}
.overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  text-align: center;
  line-height: 18px !important;
}
</style>
